import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../services/api';
import axios from 'axios';
import {
  BarChart3, TrendingUp, Briefcase, DollarSign, Clock, Award,
  MapPin, Layers, ArrowLeft, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt$ = (v) => {
  const n = Number(v) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};
const fmt$2 = (v) => {
  const n = Number(v) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
};
const fmtN = (v) => (Number(v) || 0).toLocaleString('en-US');
const fmtPct = (v) => `${(Number(v) || 0).toFixed(1)}%`;

// ---------------------------------------------------------------------------
// Small UI primitives
// ---------------------------------------------------------------------------

const StatCard = ({ label, value, sub, icon: Icon, testid, delta }) => (
  <div className="bg-white border border-gray-200 rounded shadow-sm p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-light text-gray-700 mt-1 flex items-baseline gap-2">
          <span>{value}</span>
          {delta && <Delta {...delta} />}
        </div>
        {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
      </div>
      {Icon && <Icon className="w-6 h-6 text-[#5B8DB8] opacity-70" />}
    </div>
  </div>
);

// Tiny delta badge — green when up, red when down, grey when flat.
// Caller decides whether "up" is a good thing (e.g. profit) or bad
// (e.g. expenses) via the `goodWhenUp` flag.
const Delta = ({ current, previous, format = 'number', goodWhenUp = true, compareYear }) => {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  const diff = cur - prev;
  const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '·';
  const positive = (diff > 0 && goodWhenUp) || (diff < 0 && !goodWhenUp);
  const negative = (diff < 0 && goodWhenUp) || (diff > 0 && !goodWhenUp);
  const cls = positive ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
            : negative ? 'text-red-600 bg-red-50 border-red-200'
            : 'text-gray-500 bg-gray-50 border-gray-200';
  let body;
  if (format === 'percent_point') {
    body = `${diff > 0 ? '+' : ''}${diff.toFixed(1)}pp`;
  } else if (format === 'currency') {
    const abs = Math.abs(diff);
    const compact = abs >= 1000 ? `$${Math.round(diff / 1000)}k` : `${diff > 0 ? '+' : ''}$${Math.round(diff).toLocaleString()}`;
    body = compact.startsWith('-') || compact.startsWith('+') ? compact : `${diff > 0 ? '+' : '−'}${compact.replace('-', '')}`;
  } else {
    body = `${diff > 0 ? '+' : ''}${Math.round(diff).toLocaleString()}`;
  }
  return (
    <span
      className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${cls}`}
      title={`vs ${compareYear}: ${prev.toLocaleString()}`}
      data-testid="stat-delta"
    >
      {arrow} {body}
    </span>
  );
};

const Section = ({ title, subtitle, children, testid }) => (
  <div className="bg-white border border-gray-200 rounded shadow-sm p-6" data-testid={testid}>
    <h2 className="text-lg text-[#5B8DB8] font-normal">{title}</h2>
    {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
    <div className={subtitle ? '' : 'mt-3'}>{children}</div>
  </div>
);

// Horizontal bar — width scales to the row's value vs. the section max.
const Bar = ({ value, max, color = '#5B8DB8' }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

// Renders the Financial Summary block. When `compareData` is present we
// show a second column with the comparison year's value plus a delta
// badge so the user can see both years side-by-side instead of just
// the current year's totals.
const FinRow = ({ label, current, compare, compareYear, format = 'currency', goodWhenUp = true, bold = false, big = false, testid }) => {
  const fmt = format === 'currency' ? fmt$ : (format === 'hours' ? ((v) => `${fmtN(Math.round(v))} hrs`) : fmtN);
  const hasCompare = compare !== undefined && compare !== null;
  return (
    <div className="flex justify-between items-center" data-testid={testid}>
      <span className={`text-sm ${bold ? 'font-medium text-gray-700' : 'text-gray-600'}`}>{label}</span>
      <div className="flex items-center gap-3">
        {hasCompare && (
          <span className="text-sm text-gray-400" title={`${compareYear}`}>{fmt(compare)}</span>
        )}
        <span className={`${big ? 'text-xl font-light text-emerald-600' : 'text-lg font-light text-gray-700'}`}>
          {fmt(current)}
        </span>
        {hasCompare && (
          <Delta
            current={current}
            previous={compare}
            format={format === 'currency' ? 'currency' : 'number'}
            goodWhenUp={goodWhenUp}
            compareYear={compareYear}
          />
        )}
      </div>
    </div>
  );
};

const FinancialSummaryBody = ({ data, compareData, compareYear }) => {
  const { totals, profit, labor, expenses } = data;
  const labor_cost_total = (labor.avg_cost_per_project || 0) * (totals.projects || 0);

  const cmp = compareData ? {
    revenue: compareData.profit?.total_revenue,
    expenses: compareData.expenses?.total,
    labor_cost: (compareData.labor?.avg_cost_per_project || 0) * (compareData.totals?.projects || 0),
    profit: compareData.profit?.total_profit,
    labor_hours: compareData.labor?.total_hours,
    avg_labor_cost: compareData.labor?.avg_cost_per_project,
    employees: compareData.totals?.employees,
    timeslips: compareData.totals?.labor_slips,
  } : null;

  return (
    <div className="space-y-3">
      <FinRow label="Total tracked revenue (Pre-Tax)" current={profit.total_revenue} compare={cmp?.revenue} compareYear={compareYear} testid="fin-revenue" />
      <FinRow label="Total expenses recorded" current={expenses.total} compare={cmp?.expenses} compareYear={compareYear} goodWhenUp={false} testid="fin-expenses" />
      <FinRow label="Total labor cost (timeslip-derived)" current={labor_cost_total} compare={cmp?.labor_cost} compareYear={compareYear} goodWhenUp={false} testid="fin-labor-cost" />
      <div className="border-t pt-3 mt-2">
        <FinRow label="Total profit" current={profit.total_profit} compare={cmp?.profit} compareYear={compareYear} bold big testid="fin-profit" />
      </div>
      <div className="text-xs text-gray-500 pt-2 border-t mt-2 space-y-1">
        <div className="flex justify-between" data-testid="fin-hours">
          <span>Total labor hours tracked:</span>
          <span>
            {cmp && <span className="text-gray-400 mr-3">{fmtN(Math.round(cmp.labor_hours || 0))} hrs</span>}
            <span className="text-gray-700">{fmtN(Math.round(labor.total_hours))} hrs</span>
          </span>
        </div>
        <div className="flex justify-between" data-testid="fin-avg-labor">
          <span>Avg labor cost per project:</span>
          <span>
            {cmp && <span className="text-gray-400 mr-3">{fmt$(cmp.avg_labor_cost || 0)}</span>}
            <span className="text-gray-700">{fmt$(labor.avg_cost_per_project)}</span>
          </span>
        </div>
        <div className="flex justify-between" data-testid="fin-employees">
          <span>Employees on file:</span>
          <span>
            {cmp && <span className="text-gray-400 mr-3">{fmtN(cmp.employees || 0)}</span>}
            <span className="text-gray-700">{fmtN(totals.employees)}</span>
          </span>
        </div>
        <div className="flex justify-between" data-testid="fin-timeslips">
          <span>Total timeslips:</span>
          <span>
            {cmp && <span className="text-gray-400 mr-3">{fmtN(cmp.timeslips || 0)}</span>}
            <span className="text-gray-700">{fmtN(totals.labor_slips)}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const Insights = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // null = "All time". Otherwise a number (e.g. 2026).
  const [year, setYear] = useState(null);
  // null = no comparison. Otherwise a year to fetch in parallel and
  // diff against. Mutually exclusive with year=null (no point comparing
  // an "all time" view since 'previous' would be undefined).
  const [compareYear, setCompareYear] = useState(null);
  const [compareData, setCompareData] = useState(null);
  // Cached separately so the dropdown stays populated while a new year
  // is loading (`data` is briefly null between fetches otherwise).
  const [availableYears, setAvailableYears] = useState([]);

  // Build lookup maps for the comparison-year list metrics so per-row
  // deltas are O(1) instead of N×M scans inside the render loop. Hooks
  // must run unconditionally so these live before any early returns.
  const cmpSourceCounts = useMemo(() => {
    const m = new Map();
    (compareData?.sources || []).forEach((s) => m.set(s.label, s.count));
    return m;
  }, [compareData]);
  const cmpJobTypeCounts = useMemo(() => {
    const m = new Map();
    (compareData?.job_types || []).forEach((j) => m.set(j.label, j.count));
    return m;
  }, [compareData]);

  // Items present in compareData but absent from current data — they
  // "went away" year-over-year. Hooks must precede any early returns.
  const goneSources = useMemo(() => {
    if (!compareData || !data) return [];
    const present = new Set((data.sources || []).map((s) => s.label));
    return (compareData.sources || []).filter((s) => !present.has(s.label));
  }, [compareData, data]);
  const goneJobTypes = useMemo(() => {
    if (!compareData || !data) return [];
    const present = new Set((data.job_types || []).map((j) => j.label));
    return (compareData.job_types || []).filter((j) => !present.has(j.label));
  }, [compareData, data]);

  useEffect(() => {
    if (!user?.is_admin) return;
    const load = async () => {
      setLoading(true);
      try {
        const mainUrl = year ? `${API_URL}/insights/overview?year=${year}` : `${API_URL}/insights/overview`;
        const compareUrl = (year && compareYear) ? `${API_URL}/insights/overview?year=${compareYear}` : null;
        const [mainRes, compareRes] = await Promise.all([
          axios.get(mainUrl, { headers: getAuthHeaders() }),
          compareUrl ? axios.get(compareUrl, { headers: getAuthHeaders() }) : Promise.resolve(null),
        ]);
        setData(mainRes.data);
        setCompareData(compareRes ? compareRes.data : null);
        if (Array.isArray(mainRes.data.available_years) && mainRes.data.available_years.length > 0) {
          setAvailableYears(mainRes.data.available_years);
        }
      } catch (e) {
        toast.error(`Failed to load insights: ${e.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, year, compareYear]);

  if (!user?.is_admin) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500" data-testid="insights-admin-required">Admin access required.</div>
      </Layout>
    );
  }

  // Year picker is rendered in the header even during loading / no-data
  // states so the user can change years without losing the dropdown.
  const YearPicker = () => (
    <div className="flex items-center gap-2">
      <label htmlFor="insights-year" className="text-sm text-gray-600">Year:</label>
      <select
        id="insights-year"
        value={year ?? ''}
        onChange={(e) => {
          const v = e.target.value ? Number(e.target.value) : null;
          setYear(v);
          // "All time" mode can't be compared (no second year to diff
          // against), so we clear the compare selection automatically.
          if (!v) setCompareYear(null);
          // Don't let the user compare a year to itself.
          else if (compareYear === v) setCompareYear(null);
        }}
        className="px-2 py-1.5 text-sm border rounded bg-white"
        data-testid="insights-year-select"
        disabled={loading}
      >
        <option value="">All time</option>
        {availableYears.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
      {year && availableYears.length > 1 && (
        <>
          <label htmlFor="insights-compare" className="text-sm text-gray-600 ml-2">vs:</label>
          <select
            id="insights-compare"
            value={compareYear ?? ''}
            onChange={(e) => setCompareYear(e.target.value ? Number(e.target.value) : null)}
            className="px-2 py-1.5 text-sm border rounded bg-white"
            data-testid="insights-compare-select"
            disabled={loading}
          >
            <option value="">(no comparison)</option>
            {availableYears.filter((y) => y !== year).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </>
      )}
    </div>
  );

  if (loading && !data) {
    return (
      <Layout>
        <div className="p-4 lg:p-8">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="text-lg text-[#5B8DB8] font-light">Administration</h2>
              <h1 className="text-3xl text-gray-700 font-light italic">Insights</h1>
            </div>
            <YearPicker />
          </div>
          <div className="text-gray-500" data-testid="insights-loading">Loading insights…</div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="p-8 text-gray-500 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" /> No data.
        </div>
      </Layout>
    );
  }

  // Items that existed in the comparison year but have ZERO count in the
  // current year. Surfaces revenue channels / job types that dried up
  // year-over-year — arguably the most actionable signal.
  const { totals, sources, boards, avg_days_by_board, profit, labor, expenses,
    pm_commissions, territories, job_types, lifecycle, monthly_created, top_projects } = data;
  // - Item missing from compare → green "(new)" badge.
  // - Item shrank to 0 in current → handled outside (we only iterate current).
  // - Otherwise +N / -N with the same green/red palette as Delta above.
  const RowDelta = ({ current, previousCounts, label }) => {
    if (!compareData) return null;
    if (!previousCounts.has(label)) {
      return (
        <span
          className="ml-1 text-[10px] font-medium px-1.5 py-[1px] rounded border text-emerald-600 bg-emerald-50 border-emerald-200"
          title={`Not present in ${compareYear}`}
          data-testid="row-new-badge"
        >
          new
        </span>
      );
    }
    const prev = previousCounts.get(label) || 0;
    const diff = current - prev;
    if (diff === 0) return null;
    const cls = diff > 0
      ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
      : 'text-red-600 bg-red-50 border-red-200';
    return (
      <span
        className={`ml-1 text-[10px] font-medium px-1.5 py-[1px] rounded border ${cls}`}
        title={`vs ${compareYear}: ${prev}`}
        data-testid="row-delta"
      >
        {diff > 0 ? `+${diff}` : `${diff}`}
      </span>
    );
  };

  const sourcesMax = Math.max(1, ...sources.map((s) => s.count));
  const jobTypesMax = Math.max(1, ...job_types.map((j) => j.count));
  const territoriesMax = Math.max(1, ...territories.map((t) => t.count));
  const monthlyMax = Math.max(1, ...monthly_created.map((m) => m.count));
  const pmMax = Math.max(1, ...pm_commissions.map((p) => p.commission_total));

  return (
    <Layout>
      <div className="p-4 lg:p-8" data-testid="insights-page">
        {/* Header */}
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="text-lg text-[#5B8DB8] font-light">Administration</h2>
            <h1 className="text-3xl text-gray-700 font-light italic">
              Insights{year ? <span className="text-gray-400"> · {year}{compareYear ? ` vs ${compareYear}` : ''}</span> : null}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {year && compareYear
                ? `Comparing FNOL-year ${year} against ${compareYear}. Deltas appear next to each metric.`
                : year
                ? `Project, labor, commission, and lifecycle statistics for ${year} (projects with an FNOL date in ${year}).`
                : 'Dashboard of project, labor, commission, and lifecycle statistics across the system.'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <YearPicker />
            {loading && <span className="text-xs text-gray-400" data-testid="insights-refreshing">Refreshing…</span>}
            <button
              onClick={() => navigate('/setup')}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded border border-gray-200"
              data-testid="insights-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Setup
            </button>
          </div>
        </div>

        {/* Top stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Total Projects"
            value={fmtN(totals.projects)}
            icon={Briefcase}
            testid="stat-total-projects"
            delta={compareData ? { current: totals.projects, previous: compareData.totals?.projects, compareYear } : null}
          />
          <StatCard
            label="Avg Profit per Project"
            value={fmt$(profit.avg_amount)}
            sub={`${fmtPct(profit.avg_percent)} margin · ${profit.samples} projects with value`}
            icon={TrendingUp}
            testid="stat-avg-profit"
            delta={compareData ? { current: profit.avg_amount, previous: compareData.profit?.avg_amount, compareYear, format: 'currency' } : null}
          />
          <StatCard
            label="Avg Labor Hours / Project"
            value={(labor.avg_hours_per_project || 0).toFixed(1)}
            sub={`${fmtN(Math.round(labor.total_hours))} hrs tracked total`}
            icon={Clock}
            testid="stat-avg-labor"
            delta={compareData ? { current: labor.avg_hours_per_project, previous: compareData.labor?.avg_hours_per_project, compareYear, goodWhenUp: false } : null}
          />
          <StatCard
            label="Avg Expenses / Project"
            value={fmt$(expenses.avg_per_project)}
            sub={`${fmt$(expenses.total)} total recorded`}
            icon={DollarSign}
            testid="stat-avg-expenses"
            delta={compareData ? { current: expenses.avg_per_project, previous: compareData.expenses?.avg_per_project, compareYear, format: 'currency', goodWhenUp: false } : null}
          />
        </div>

        {/* Boards + lifecycle */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Section title="Projects by Board" subtitle="Live counts across the 4 project lists." testid="section-boards">
            <div className="space-y-3">
              {boards.map((b) => {
                const aging = avg_days_by_board.find((a) => a.key === b.key);
                return (
                  <div key={b.key} className="flex items-center gap-4">
                    <div className="w-44 text-sm text-gray-700">{b.label}</div>
                    <div className="flex-1">
                      <Bar value={b.count} max={Math.max(1, ...boards.map((x) => x.count))} />
                    </div>
                    <div className="w-12 text-right text-sm text-gray-700 font-medium">{b.count}</div>
                    <div className="w-32 text-right text-xs text-gray-500">
                      {aging?.samples ? `${aging.avg_days}d avg age` : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section title="Project Lifecycle" subtitle="Average elapsed days between key milestones." testid="section-lifecycle">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">FNOL → Completion</div>
                <div className="text-2xl font-light text-gray-700 mt-1">{lifecycle.avg_days_fnol_to_completion}d</div>
                <div className="text-xs text-gray-400">{lifecycle.samples_fnol_to_completion} samples</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Completion → Paid</div>
                <div className="text-2xl font-light text-gray-700 mt-1">{lifecycle.avg_days_completion_to_paid}d</div>
                <div className="text-xs text-gray-400">{lifecycle.samples_completion_to_paid} samples</div>
              </div>
            </div>
            <div className="mt-6">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Projects Created — Last 12 Months</div>
              <div className="flex items-end gap-1 h-24">
                {monthly_created.length === 0 ? (
                  <div className="text-xs text-gray-400">No data</div>
                ) : monthly_created.map((m) => (
                  <div key={m.month} className="flex-1 flex flex-col items-center" title={`${m.month}: ${m.count}`}>
                    <div className="w-full bg-[#5B8DB8] rounded-t" style={{ height: `${(m.count / monthlyMax) * 100}%`, minHeight: 2 }} />
                    <div className="text-[10px] text-gray-500 mt-1 truncate">{m.month.slice(5)}</div>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        </div>

        {/* Sources + Job Types */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Section title="Project Sources" subtitle="Where projects come from." testid="section-sources">
            {sources.length === 0 ? (
              <div className="text-sm text-gray-400">No sources recorded.</div>
            ) : (
              <div className="space-y-2">
                {sources.map((s) => (
                  <div key={s.label} className="flex items-center gap-3">
                    <div className="w-40 text-sm text-gray-700 truncate" title={s.label}>{s.label}</div>
                    <div className="flex-1"><Bar value={s.count} max={sourcesMax} color="#4ECFDB" /></div>
                    <div className="w-20 text-right text-sm text-gray-700 font-medium">
                      {s.count}<RowDelta current={s.count} previousCounts={cmpSourceCounts} label={s.label} />
                    </div>
                    <div className="w-14 text-right text-xs text-gray-500">{fmtPct(s.percent)}</div>
                  </div>
                ))}
                {goneSources.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5" data-testid="gone-sources-heading">
                      No longer present (in {compareYear})
                    </div>
                    {goneSources.map((s) => (
                      <div key={s.label} className="flex items-center gap-3 text-gray-400" data-testid={`gone-source-${s.label}`}>
                        <div className="w-40 text-sm truncate" title={s.label}>{s.label}</div>
                        <div className="flex-1 h-px bg-gray-100" />
                        <div className="w-20 text-right text-sm font-medium">
                          0
                          <span className="ml-1 text-[10px] font-medium px-1.5 py-[1px] rounded border text-red-600 bg-red-50 border-red-200" title={`vs ${compareYear}: ${s.count}`}>
                            −{s.count}
                          </span>
                        </div>
                        <div className="w-14 text-right text-xs">gone</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Section>

          <Section title="Job Types" subtitle="Top 15 by project count, with total subtotal value." testid="section-job-types">
            {job_types.length === 0 ? (
              <div className="text-sm text-gray-400">No job types recorded.</div>
            ) : (
              <div className="space-y-2">
                {job_types.map((j) => (
                  <div key={j.label} className="flex items-center gap-3">
                    <div className="w-40 text-sm text-gray-700 truncate" title={j.label}>{j.label}</div>
                    <div className="flex-1"><Bar value={j.count} max={jobTypesMax} color="#E8964F" /></div>
                    <div className="w-20 text-right text-sm text-gray-700 font-medium">
                      {j.count}<RowDelta current={j.count} previousCounts={cmpJobTypeCounts} label={j.label} />
                    </div>
                    <div className="w-24 text-right text-xs text-gray-500">{fmt$(j.total_value)}</div>
                  </div>
                ))}
                {goneJobTypes.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5" data-testid="gone-job-types-heading">
                      No longer present (in {compareYear})
                    </div>
                    {goneJobTypes.map((j) => (
                      <div key={j.label} className="flex items-center gap-3 text-gray-400" data-testid={`gone-job-type-${j.label}`}>
                        <div className="w-40 text-sm truncate" title={j.label}>{j.label}</div>
                        <div className="flex-1 h-px bg-gray-100" />
                        <div className="w-20 text-right text-sm font-medium">
                          0
                          <span className="ml-1 text-[10px] font-medium px-1.5 py-[1px] rounded border text-red-600 bg-red-50 border-red-200" title={`vs ${compareYear}: ${j.count}`}>
                            −{j.count}
                          </span>
                        </div>
                        <div className="w-24 text-right text-xs">gone</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Section>
        </div>

        {/* Territories */}
        <div className="mb-6">
          <Section title="Territories" subtitle="Project count, total project value, and average per territory." testid="section-territories">
            {territories.length === 0 ? (
              <div className="text-sm text-gray-400">No territories recorded.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="territories-table">
                  <thead className="border-b-2 border-gray-200 text-gray-600">
                    <tr>
                      <th className="text-left p-2 font-normal">Territory</th>
                      <th className="text-left p-2 font-normal w-2/5"></th>
                      <th className="text-right p-2 font-normal w-24">Projects</th>
                      <th className="text-right p-2 font-normal w-36">Total Value</th>
                      <th className="text-right p-2 font-normal w-32">Avg / Project</th>
                    </tr>
                  </thead>
                  <tbody>
                    {territories.map((t) => (
                      <tr key={t.label} className="border-b hover:bg-gray-50">
                        <td className="p-2 text-gray-700">{t.label}</td>
                        <td className="p-2"><Bar value={t.count} max={territoriesMax} color="#E89B4C" /></td>
                        <td className="p-2 text-right text-gray-700">{t.count}</td>
                        <td className="p-2 text-right text-gray-700">{fmt$(t.total_value)}</td>
                        <td className="p-2 text-right text-gray-500">{fmt$(t.avg_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>

        {/* PMs */}
        <div className="mb-6">
          <Section
            title="Production Manager Commissions"
            subtitle={year
              ? `Commission earned for ${year} projects only, computed using Job Type rates × PM share %.`
              : "Total commission earned across all projects, computed using Job Type rates × PM share %."}
            testid="section-pms"
          >
            {pm_commissions.length === 0 ? (
              <div className="text-sm text-gray-400">No PM assignments yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pm-commissions-table">
                  <thead className="border-b-2 border-gray-200 text-gray-600">
                    <tr>
                      <th className="text-left p-2 font-normal">Production Manager</th>
                      <th className="text-left p-2 font-normal w-2/5"></th>
                      <th className="text-right p-2 font-normal w-24">Projects</th>
                      <th className="text-right p-2 font-normal w-36">Total Commission</th>
                      <th className="text-right p-2 font-normal w-32">Avg / Project</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pm_commissions.map((p) => (
                      <tr key={p.name} className="border-b hover:bg-gray-50">
                        <td className="p-2 text-gray-700 flex items-center gap-2"><Award className="w-4 h-4 text-[#5B8DB8]" />{p.name}</td>
                        <td className="p-2"><Bar value={p.commission_total} max={pmMax} color="#5B8DB8" /></td>
                        <td className="p-2 text-right text-gray-700">{p.projects}</td>
                        <td className="p-2 text-right text-gray-700">{fmt$2(p.commission_total)}</td>
                        <td className="p-2 text-right text-gray-500">{fmt$2(p.commission_avg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>

        {/* Top projects + Financial summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Section title="Top 10 Projects by Value" subtitle={year ? `Among ${year} projects (by FNOL year).` : null} testid="section-top-projects">
            {top_projects.length === 0 ? (
              <div className="text-sm text-gray-400">No projects with a pre-tax value yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b-2 border-gray-200 text-gray-600">
                    <tr>
                      <th className="text-left p-2 font-normal">Project #</th>
                      <th className="text-left p-2 font-normal">Customer</th>
                      <th className="text-left p-2 font-normal w-20">Board</th>
                      <th className="text-right p-2 font-normal w-32">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top_projects.map((p) => (
                      <tr key={p.project_id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/projects/${p.project_id}`)}>
                        <td className="p-2 text-[#5B8DB8]">{p.project_id}</td>
                        <td className="p-2 text-gray-700 truncate max-w-xs" title={p.customer}>{p.customer}</td>
                        <td className="p-2 text-gray-500 text-xs uppercase">{p.status}</td>
                        <td className="p-2 text-right text-gray-700">{fmt$(p.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section title="Financial Summary" subtitle={year ? `Across ${year} projects with a recorded subtotal.${compareYear ? ` Compared with ${compareYear}.` : ''}` : "Across all projects with a recorded subtotal."} testid="section-financial">
            <FinancialSummaryBody
              data={data}
              compareData={compareData}
              compareYear={compareYear}
            />
          </Section>
        </div>
      </div>
    </Layout>
  );
};

export default Insights;
